import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  Done
} from '@mui/icons-material';
import { Task, TaskFormData, getLoyaltyStateFromLocalStorage, updateLoyaltyStateInLocalStorage, removeLoyaltyStateFromLocalStorage } from './utils';
import { setTasks } from "../../stateManagement/actions/loyaltyManagementActions";
import MultiCategoryFilter from "../../components/dms_common/MultiCategoryFilter";
import { normalizeText, capitalizeFirstLetter } from '../../utils/utils';
import { openPopup } from "../../stateManagement/actions/popupActions";
import { updateTask } from "../../services/loyaltyService";
import { logAnalyticsEvent } from "../../../src/firebase";

interface TaskDetailsProps {
  activeModules?: any[];
  configData?: any[];
}

const TaskDetails: React.FC<TaskDetailsProps> = ({ activeModules = [], configData = [] }) => {
  const savedState = getLoyaltyStateFromLocalStorage();
  const [sortBy, setSortBy] = useState(savedState?.sortBy || 'creation-time-new-old');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    savedState?.startDate || dayjs().startOf('month'),
    savedState?.endDate || dayjs().endOf('month')
  ]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pointsFilter, setPointsFilter] = useState<string>('all');
  const [isDateActionLoading, setIsDateActionLoading] = useState(false);
  const [loadingTaskId, setLoadingTaskId] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('status');
  const [filterSearchValue, setFilterSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(savedState?.selectedStatuses || []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tasks = [], selectedModule } = useSelector((state: any) => state.loyaltyManagement);

  useEffect(() => {
    updateLoyaltyStateInLocalStorage({
      selectedModule,
      selectedStatuses,
      sortBy,
      startDate: dateRange[0],
      endDate: dateRange[1]
    });
  }, [selectedStatuses, sortBy, selectedModule, dateRange]);

  const statusCounts = useMemo(() => {
    const counts = { active: 0, inactive: 0 };
    if (!selectedModule) return counts;

    let moduleTasks = tasks.filter((task: Task) => normalizeText(task.taskName) === normalizeText(selectedModule));

    if (dateRange[0] && dateRange[1]) {
      moduleTasks = moduleTasks.filter((task: Task) => {
        const startDate = dayjs(task.startTime);
        return (
          startDate.isSame(dateRange[0], 'day') ||
          startDate.isSame(dateRange[1], 'day') ||
          (startDate.isAfter(dateRange[0]) && startDate.isBefore(dateRange[1]))
        );
      });
    }
    moduleTasks.forEach((task: Task) => {
      const status = task.activeStatus?.toLowerCase();
      if (status === 'active') counts.active++;
      if (status === 'inactive') counts.inactive++;
    });

    return counts;
  }, [tasks, selectedModule, dateRange]);

  const filters: any[] = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { id: 'active', label: 'Active', count: statusCounts.active },
        { id: 'inactive', label: 'Inactive', count: statusCounts.inactive },
      ],
      selectedOptions: selectedStatuses,
      setSelectedOptions: setSelectedStatuses,
    },
  ];

  const filteredTasks = useMemo(() => {
    if (!selectedModule) return [];

    let filtered = tasks.filter((task: Task) => normalizeText(task.taskName) === normalizeText(selectedModule));

    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((task: Task) => {
        const startDate = dayjs(task.startTime);
        return (
          startDate.isSame(dateRange[0], 'day') ||
          startDate.isSame(dateRange[1], 'day') ||
          (startDate.isAfter(dateRange[0]) && startDate.isBefore(dateRange[1]))
        );
      });
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((task: Task) => selectedStatuses.includes(task.activeStatus.toLowerCase()));
    }

    if (sortBy) {
      filtered.sort((a: Task, b: Task) => {
        switch (sortBy) {
          case 'start-date-new-old':
            return dayjs(b.startTime).isAfter(dayjs(a.startTime)) ? 1 : -1;
          case 'start-date-old-new':
            return dayjs(a.startTime).isAfter(dayjs(b.startTime)) ? 1 : -1;
          case 'creation-time-new-old':
            return dayjs(b.creationTime).isAfter(dayjs(a.creationTime)) ? 1 : -1;
          case 'creation-time-old-new':
            return dayjs(a.creationTime).isAfter(dayjs(b.creationTime)) ? 1 : -1;
          default:
            return dayjs(b.creationTime).isAfter(dayjs(a.creationTime)) ? 1 : -1;
        }
      });
    }
    return filtered;
  }, [tasks, selectedModule, dateRange, statusFilter, pointsFilter, sortBy, selectedStatuses]);

  const prepareTaskData = (task: Task) => {
    const outletBifurcationsList = task.extendedAttributes?.outletBifurcationsList || [];
    const hasOutletSelections = outletBifurcationsList.some((outlet: any) => {
      return (outlet.outletCode?.length > 0) || (outlet.outletClass?.length > 0) || (outlet.channel?.length > 0) || (outlet.distributorDetails?.length > 0);
    });

    const allOutletsNull = !hasOutletSelections || !outletBifurcationsList || outletBifurcationsList.length === 0;

    const taskData: TaskFormData = {
      taskName: task.displayName || '',
      category: task.taskName || '',
      activeStatus: task.activeStatus || 'INACTIVE',
      template: task.extendedAttributes?.template || '',
      description: task.description || '',
      thresholdValue: task.threshold?.toString() || '',
      pointsReward: task.points?.toString() || '',
      redirectionPlaceholder: task.extendedAttributes?.redirection?.id || '',
      redirection: task.extendedAttributes?.redirection || null,
      startDate: task.startTime ? dayjs(task.startTime).format('YYYY-MM-DD') : '',
      endDate: task.endTime ? dayjs(task.endTime).format('YYYY-MM-DD') : '',
      applyToAll: allOutletsNull,
      outletBifurcationsList: outletBifurcationsList,
      guidanceType: task.extendedAttributes?.guidanceType || 'written',
      guidanceContent: task.extendedAttributes?.guidanceContent || '',
      bannerLink: task.extendedAttributes?.bannerLink || ''
    };

    return taskData;
  };

  const handleTaskAction = (task: Task, action: 'clone' | 'edit', e: React.MouseEvent) => {
    e.stopPropagation();
    const eventParams = {
      task_id: task.id,
      task_status: task.activeStatus,
      points: task.points,
      task_name: task.taskName
    };

    const taskData = prepareTaskData(task);
    const navigateState: any = {
      taskData,
      activeModules,
      configData
    };

    if (action === 'clone') {
      navigateState.isCloneMode = true;
      logAnalyticsEvent("clone_task_click", eventParams);
    } else {
      navigateState.isEditMode = true;
      navigateState.taskId = task.id;
      logAnalyticsEvent("edit_task_click", eventParams);
    }

    navigate("create", { state: navigateState });
  };

  const handleCloneTask = (task: Task, e: React.MouseEvent) =>
    handleTaskAction(task, 'clone', e);

  const handleEditTask = (task: Task, e: React.MouseEvent) =>
    handleTaskAction(task, 'edit', e);

  const handleActivateDeactivateTask = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();

    if (loadingTaskId === task.id) return;

    const newStatus = task.activeStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const action = newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate';
    const eventParams = {
      task_id: task.id,
      current_status: task.activeStatus,
      new_status: newStatus,
      points: task.points,
      task_name: task.taskName
    };

    try {
      openPopup(
        'Alert',
        `Are you sure you want to ${action.toLowerCase()} this task?`,
        async () => {
          setLoadingTaskId(task.id);
          try {
            await updateTask({ id: task.id, activeStatus: newStatus });
            const updatedTasks = tasks.map((t: Task) =>
              t.id === task.id ? { ...t, activeStatus: newStatus } : t
            );
            dispatch(setTasks(updatedTasks));
            if (newStatus === 'ACTIVE') {
              logAnalyticsEvent("activate_task_click", eventParams);
            } else {
              logAnalyticsEvent("deactivate_task_click", eventParams);
            }
            openPopup('Alert', `Task ${action.toLowerCase()}d successfully!`);
          } catch (error) {
            console.error(`Error ${action.toLowerCase()}ing task:`, error);
            openPopup('Error', `Failed to ${action.toLowerCase()} task. Please try again.`);
          } finally {
            setLoadingTaskId(null);
          }
        }
      );
    } catch (error) {
      console.error('Error showing confirmation:', error);
    }
  };

  const handleClearAll = async () => {
    if (isDateActionLoading) return;
    setIsDateActionLoading(true);
    try {
      const currentState = getLoyaltyStateFromLocalStorage();
      updateLoyaltyStateInLocalStorage({
        selectedModule: currentState?.selectedModule,
        sortBy: 'creation-time-new-old',
        startDate: dayjs().startOf('month'),
        endDate: dayjs().endOf('month')
      });
      setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
      setStatusFilter('all');
      setPointsFilter('all');
      setSortBy('creation-time-new-old');
    } finally {
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsDateActionLoading(false);
    }
  };

  return (
    <Box sx={{
      borderRadius: 2,
      border: '1px solid #DAE3E6',
      bgcolor: 'white',
      p: 1,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: '70vh'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#02C7AF',
            fontWeight: 600,
            mb: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '16px',
          }}
        >
          • {selectedModule ? capitalizeFirstLetter(selectedModule) : ''} Task Repo
        </Typography>
        <FormControl size="small" sx={{ width: 240 }}>
          <InputLabel sx={{ fontSize: '14px' }}>Sort by</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label="Sort by"
            sx={{
              fontSize: '13px',
              height: '32px',
            }}
            IconComponent={ExpandIcon}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    fontSize: '14px',
                    minHeight: '28px',
                  }
                }
              }
            }}
          >
            <MenuItem value="creation-time-new-old">Creation Time (New to Old)</MenuItem>
            <MenuItem value="creation-time-old-new">Creation Time (Old to New)</MenuItem>
            <MenuItem value="start-date-new-old">Start Date (New to Old)</MenuItem>
            <MenuItem value="start-date-old-new">Start Date (Old to New)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: '3%', mb: 1, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', flex: 6, gap: '7px', width: '70%', alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateRangePicker
              value={dateRange}
              onChange={(newValue) => setDateRange(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(startProps, endProps) => (
                <React.Fragment>
                  <TextField
                    {...startProps}
                    size="small"
                    label="From Date"
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '125px',
                        height: '32px'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      }
                    }}
                  />
                  <Box sx={{ mx: 0.5 }}> </Box>
                  <TextField
                    {...endProps}
                    size="small"
                    label="To Date"
                    sx={{
                      '& .MuiInputBase-root': {
                        width: '125px',
                        height: '32px'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      }
                    }}
                  />
                </React.Fragment>
              )}
            />
          </LocalizationProvider>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '7px', ml: 0.5 }}>
            <Button
              variant="outlined"
              sx={{
                backgroundColor: '#f0fbff',
                boxShadow: 'none',
                height: '32px',
                minHeight: '32px',
                fontSize: '14px',
                padding: '4px 12px',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
              onClick={handleClearAll}
              disabled={isDateActionLoading}
            >
              Reset
            </Button>
          </Box>
        </Box>
        <Box sx={{
          '& .manage_secondary_orders_filter': {
            height: '30px',
            alignItems: 'center',
            '& .MuiFormControl-root': {
              height: '30px',
            },
            '& .MuiInputBase-root': {
              height: '32px',
              minHeight: '32px',
              '& .MuiSelect-select': {
                height: '28px !important',
                minHeight: '28px !important',
                padding: '4px 12px !important',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
              }
            }
          }
        }}>
          <MultiCategoryFilter
            isOpen={filterOpen}
            setIsOpen={setFilterOpen}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            filters={filters}
            label="Status"
            searchValue={filterSearchValue}
            onSearchChange={setFilterSearchValue}
            showSearch={true}
          />
        </Box>
      </Box>
      {/* Tasks List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 200,
          maxHeight: 'calc(100vh - 350px)'
        }}
      >
        <Stack spacing={1.5}>
          {filteredTasks.length > 0 ? (
            filteredTasks.map((task: Task) => (
              <Card
                key={task.id}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  bgcolor: 'white',
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  '& .MuiCardContent-root': {
                    paddingBottom: '8px !important',
                    '&:last-child': {
                      paddingBottom: '8px !important',
                    }
                  }
                }}
              >
                <CardContent sx={{ p: 1.25, pb: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: '15px' }}>
                        {task.id}
                        <span style={{ fontWeight: 600, fontSize: '16px', color: '#99A1AF' }}> • </span>
                      </span>
                      <Chip
                        label={task.activeStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: task.activeStatus === 'ACTIVE' ? '#dcfce6' : '#F3F4F6',
                          color: task.activeStatus === 'ACTIVE' ? '#008236' : '#364153',
                          fontWeight: 500,
                          fontSize: '12px',
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloneIcon fontSize="small" />}
                        onClick={(e) => handleCloneTask(task, e)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#02C7AF',
                          color: '#02C7AF',
                          fontSize: '12px',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            borderColor: '#00ACC1',
                            bgcolor: 'rgba(0, 188, 212, 0.05)',
                          },
                        }}
                      >
                        Clone
                      </Button>
                      {task.activeStatus === 'ACTIVE' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DeleteIcon fontSize="small" />}
                          onClick={(e) => handleActivateDeactivateTask(task, e)}
                          disabled={loadingTaskId === task.id}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#d32f2f',
                            color: '#d32f2f',
                            fontSize: '12px',
                            minWidth: 'auto',
                            px: 1,
                            '&:hover': {
                              borderColor: '#b71c1c',
                              bgcolor: 'rgba(211, 47, 47, 0.05)',
                              color: '#d32f2f',
                            },
                            '&:disabled': {
                              borderColor: '#d32f2f',
                              color: '#d32f2f',
                              opacity: 0.6,
                            },
                          }}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Done fontSize="small" />}
                          onClick={(e) => handleActivateDeactivateTask(task, e)}
                          disabled={loadingTaskId === task.id}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#008236',
                            color: '#008236',
                            fontSize: '12px',
                            minWidth: 'auto',
                            px: 1,
                            '&:hover': {
                              borderColor: '#008236',
                              bgcolor: 'rgba(0, 130, 54, 0.05)',
                              color: '#008236',
                            },
                            '&:disabled': {
                              borderColor: '#008236',
                              color: '#008236',
                              opacity: 0.6,
                            },
                          }}
                        >
                          Activate
                        </Button>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={(e) => handleEditTask(task, e)}
                        sx={{
                          color: '#02C7AF',
                          fontWeight: 500,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textTransform: 'none',
                          fontSize: '12px',
                          minWidth: 'auto',
                          '&:hover': {
                            bgcolor: '#3BCDB7',
                            color: '#fff',
                          },
                        }}
                      >
                        Edit Task
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#4A5565' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          Start Date: {dayjs(task.startTime).format('DD-MM-YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#4A5565' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          End Date: {dayjs(task.endTime).format('DD-MM-YYYY')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#99A1AF', fontSize: '16px', fontWeight: 600 }}>
                      •
                    </Box>
                    <Chip
                      label={`Points: ${task.points}`}
                      size="small"
                      sx={{
                        bgcolor: '#CBFBF2',
                        color: '#519188',
                        fontWeight: 500,
                        fontSize: '12px',
                      }}
                    />
                  </Box>

                  {/* Task description */}
                  <Typography variant="body1" sx={{ lineHeight: 1.5, fontSize: '12px', color: '#333' }}>
                    {task.extendedAttributes?.preview || task.description}
                  </Typography>
                </CardContent>
              </Card>
            ))
          ) : (
            selectedModule && (
              <Typography variant="body1" sx={{ textAlign: 'center', color: '#666', py: 10 }}>
                No tasks found
              </Typography>
            )
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default TaskDetails;